import React, { useRef } from 'react';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData, categoryColors } from '../../../data/eventsData';

const getImpactStyle = (impact: string): string => {
  const styles: Record<string, string> = {
    'CRITICAL': 'bg-data-red text-white',
    'HIGH': 'bg-data-green text-black',
    'MEDIUM': 'bg-data-amber text-black',
    'LOW': 'bg-gray-600 text-white',
  };
  return styles[impact] || 'bg-gray-600 text-white';
};

interface TimelinePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const TimelinePanel: React.FC<TimelinePanelProps> = ({ isMinimized, onMinimize }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { timeline } = eventsData;

  const pastEvents = timeline.filter(e => !e.future);
  const futureEvents = timeline.filter(e => e.future);
  const currentYear = 2025;

  // Group events by year
  const years = Array.from(new Set(timeline.map(e => e.year))).sort();

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  return (
    <Panel title="TIMELINE" subtitle="2016-2030" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{timeline.length}</div>
            <div className="text-xs text-gray-500">Events</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-data-green/30">
            <div className="text-2xl font-mono font-bold text-data-green">{pastEvents.length}</div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-data-amber/30">
            <div className="text-2xl font-mono font-bold text-data-amber">{futureEvents.length}</div>
            <div className="text-xs text-gray-500">Upcoming</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-green">2027</div>
            <div className="text-xs text-gray-500">IG Target</div>
          </div>
        </div>

        {/* Scroll Controls */}
        <div className="flex justify-between items-center mb-3">
          <button
            onClick={() => scroll('left')}
            className="px-3 py-2 text-xs text-gray-400 hover:text-white bg-terminal-bg/40 rounded flex items-center gap-1.5 border border-terminal-border/30"
          >
            <ChevronLeft size={14} /> Earlier
          </button>
          <div className="text-xs text-gray-500 flex items-center gap-4">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-data-green rounded-full" /> Past</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-data-red rounded-full animate-pulse" /> Now</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 border border-data-amber rounded-full" /> Future</span>
          </div>
          <button
            onClick={() => scroll('right')}
            className="px-3 py-2 text-xs text-gray-400 hover:text-white bg-terminal-bg/40 rounded flex items-center gap-1.5 border border-terminal-border/30"
          >
            Later <ChevronRight size={14} />
          </button>
        </div>

        {/* Timeline Container */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar"
        >
          <div className="flex items-start h-full min-w-max px-4">
            {years.map((year) => {
              const yearEvents = timeline.filter(e => e.year === year);
              const isCurrent = year === currentYear;
              const isFuture = year > currentYear;

              return (
                <div key={year} className="flex flex-col items-center mx-2" style={{ minWidth: '130px' }}>
                  {/* Year Label */}
                  <div
                    className={`text-lg font-mono font-bold mb-2 ${
                      isCurrent
                        ? 'text-data-red'
                        : isFuture
                          ? 'text-data-amber'
                          : 'text-data-green'
                    }`}
                  >
                    {year}
                    {isCurrent && <span className="ml-1 text-xs">NOW</span>}
                  </div>

                  {/* Timeline Line */}
                  <div className="relative w-full flex items-center justify-center mb-2">
                    <div
                      className={`absolute top-1/2 left-0 right-0 h-0.5 ${
                        isFuture ? 'border-t border-dashed border-data-amber/50' : 'bg-data-green/50'
                      }`}
                    />
                    <div
                      className={`relative z-10 w-4 h-4 rounded-full border-2 ${
                        isCurrent
                          ? 'bg-data-red border-data-red animate-pulse'
                          : isFuture
                            ? 'bg-terminal-bg border-data-amber'
                            : 'bg-data-green border-data-green'
                      }`}
                    />
                  </div>

                  {/* Event Cards */}
                  <div className="space-y-2 w-full">
                    {yearEvents.map((event, eventIdx) => (
                      <div
                        key={eventIdx}
                        className={`p-2 rounded-lg border ${
                          event.future
                            ? 'bg-terminal-bg/40 border-dashed border-data-amber/30'
                            : 'bg-terminal-bg/40 border-terminal-border/30'
                        }`}
                      >
                        <div className="text-sm font-semibold text-white leading-tight mb-1.5">
                          {event.event}
                        </div>
                        <div className="flex items-center justify-between">
                          <span
                            className="text-xs font-medium"
                            style={{ color: categoryColors[event.category] }}
                          >
                            {event.category}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${getImpactStyle(event.impact)}`}>
                            {event.impact}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-3 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default TimelinePanel;
